/**
 * VENDORED — sync with tara-client copy (P3: tara-client/src/lib/viewer-runtime.ts).
 *
 * The in-report viewer runtime. DEPENDENCY-FREE ES2017: this function is
 * STRINGIFIED by build-srcdoc.ts and inlined into the sandboxed report
 * iframe, so it must never reference module scope, imports, or syntax newer
 * than ES2017 (no optional chaining / nullish coalescing).
 *
 * Behaviour (per plan §Viewer):
 *  - 3-tier slide discovery: section.slide → tall direct children of the
 *    scroll container → single-scroll fallback.
 *  - Normalises the container to scroll-snap-type y mandatory +
 *    scroll-snap-stop always; slides taller than the viewport relax to
 *    `normal` so their interiors stay reachable.
 *  - Keyboard (arrows / PageUp / PageDown / Space / Home / End), click zones
 *    (bottom 12% = next, top 8% = prev; suppressed on links + controls).
 *  - Scrolls/observes the element that really scrolls (usually the document;
 *    the contract's <main class="report"> wrapper does not scroll).
 *    IntersectionObserver + scroll events only TRIGGER an index sync; the
 *    index itself is derived from slide geometry (activeIndex) → postMessage
 *    {type:'slide', index, total}; accepts {type:'goto', index}.
 *  - data-expandable deep-dive blocks: hidden when the feature is off,
 *    otherwise wrapped in an animated collapsible styled from the report's
 *    own CSS custom props; expanding relaxes snap and posts {type:'expand'}.
 *  - Posts a {type:'compat', slides, warnings} report after init.
 *  - Rewrites link clicks to postMessage {type:'link', href}; the parent
 *    vets and window.opens. Respects prefers-reduced-motion.
 *
 * P3 — EDIT MODE (agency workbench). When config.mode === 'edit' the runtime
 * makes the DEEPEST text-bearing leaves (pure text nodes only — never
 * containers, never inside <svg>, never interactive controls) editable via
 * contenteditable="plaintext-only", so employees can fix wording/numbers but
 * physically cannot break the markup structure. It tracks an edit count and
 * posts {type:'edit-state', dirty, editable}; accepts {type:'request-serialize'}
 * (posts {type:'serialized', html, dirty}) and {type:'revert-all'}. Paging /
 * snap / reveal-animation are disabled in edit mode so they don't fight the
 * caret. The parent (ReportWorkbench) strips injected artifacts from the
 * serialized HTML before POSTing a new version (see report-edit-serialize.ts).
 */
import type { ViewerRuntimeConfig } from './portal-contracts';

export function initViewerRuntime(config: ViewerRuntimeConfig): void {
  'use strict';
  var doc = document;
  var win = window;
  var warnings: string[] = [];
  var slides: HTMLElement[] = [];
  var container: HTMLElement | null = null;
  var containerIsRoot = false;
  var currentIndex = -1;
  var snapMode = true;

  var reduceMotion = false;
  try {
    reduceMotion = !!(
      win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  } catch (e) {
    reduceMotion = false;
  }

  function post(message: object): void {
    try {
      var payload: any = message;
      payload.source = 'tara-viewer';
      win.parent.postMessage(payload, '*');
    } catch (e) {
      /* parent gone */
    }
  }

  function toArray(list: ArrayLike<Element>): HTMLElement[] {
    var out: HTMLElement[] = [];
    for (var i = 0; i < list.length; i++) out.push(list[i] as HTMLElement);
    return out;
  }

  function viewportHeight(): number {
    return win.innerHeight || doc.documentElement.clientHeight || 0;
  }

  // ---------- slide discovery (3 tiers) ----------

  function commonAncestor(nodes: HTMLElement[]): HTMLElement {
    var node: HTMLElement | null = nodes[0];
    while (node && node !== doc.body) {
      var holdsAll = true;
      for (var i = 1; i < nodes.length; i++) {
        if (!node.contains(nodes[i])) {
          holdsAll = false;
          break;
        }
      }
      if (holdsAll && node !== nodes[0]) return node;
      if (holdsAll && nodes.length === 1) return node.parentElement || doc.body;
      node = node.parentElement;
    }
    return doc.body;
  }

  function scrollRoot(): HTMLElement {
    return (doc.scrollingElement as HTMLElement) || doc.documentElement;
  }

  /**
   * The element that ACTUALLY scrolls the slides: the nearest ancestor (from
   * the slides' common parent up) with overflow-y auto/scroll whose content
   * overflows it. Null means the document itself scrolls.
   *
   * The contract wraps slides in a plain <main class="report"> that grows
   * with its content while the document scrolls. Treating that wrapper as
   * the scroll container broke everything: as IntersectionObserver root it
   * never clips, so every slide "intersects" on the first callback and the
   * last one wins (index N-1 on load); and scrollTo on it is a no-op, so
   * Next/Previous moved the counter but never the pane.
   */
  function findScrollHost(start: HTMLElement | null): HTMLElement | null {
    var node: HTMLElement | null = start;
    while (node && node !== doc.body && node !== doc.documentElement) {
      var overflowY = '';
      try {
        overflowY = win.getComputedStyle(node).overflowY;
      } catch (e) {
        overflowY = '';
      }
      if (
        (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
        node.scrollHeight > node.clientHeight + 1
      ) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }

  function discover(): void {
    // Tier 1 — the contract: <section class="slide">.
    var tier1 = toArray(doc.querySelectorAll('section.slide'));
    if (tier1.length >= 2) {
      slides = tier1;
      container = commonAncestor(tier1);
      return;
    }
    // Tier 2 — direct children of the scroll container that look like pages
    // (>= 60% of the viewport tall).
    var root = scrollRoot();
    var candidates: HTMLElement[] = [];
    var scan: HTMLElement[] = [doc.body, root];
    var mains = toArray(doc.querySelectorAll('main, [data-scroll], .stage ~ *'));
    for (var m = 0; m < mains.length; m++) scan.push(mains[m]);
    var vh = viewportHeight();
    for (var s = 0; s < scan.length; s++) {
      var parent = scan[s];
      if (!parent) continue;
      var kids: HTMLElement[] = [];
      for (var c = 0; c < parent.children.length; c++) {
        var kid = parent.children[c] as HTMLElement;
        if (vh > 0 && kid.offsetHeight >= vh * 0.6) kids.push(kid);
      }
      if (kids.length >= 2 && kids.length > candidates.length) {
        candidates = kids;
        container = parent;
      }
    }
    if (candidates.length >= 2) {
      slides = candidates;
      warnings.push('slides-inferred-from-children');
      return;
    }
    // Tier 3 — plain scroll fallback; no paging, chrome shows no dots.
    slides = [];
    container = root;
    snapMode = false;
    warnings.push('single-scroll-fallback');
  }

  // ---------- snap normalisation ----------

  function isTall(slide: HTMLElement): boolean {
    var vh = viewportHeight();
    return vh > 0 && slide.offsetHeight > vh + 8;
  }

  function relaxTallSlides(initial: boolean): void {
    for (var i = 0; i < slides.length; i++) {
      if (isTall(slides[i])) {
        slides[i].style.scrollSnapStop = 'normal';
        if (initial) warnings.push('tall-slide:' + i);
      } else if (slides[i].getAttribute('data-tara-expanded') !== '1') {
        slides[i].style.scrollSnapStop = 'always';
      }
    }
  }

  function normalise(): void {
    if (!snapMode || !container) return;
    var host = containerIsRoot ? doc.documentElement : container;
    host.style.scrollSnapType = 'y mandatory';
    for (var i = 0; i < slides.length; i++) {
      var st = slides[i].style;
      if (!st.scrollSnapAlign) st.scrollSnapAlign = 'start';
    }
    relaxTallSlides(true);
  }

  // ---------- navigation ----------

  function scrollTopOf(slide: HTMLElement): number {
    if (!container || containerIsRoot) {
      var rect = slide.getBoundingClientRect();
      return rect.top + (win.pageYOffset || scrollRoot().scrollTop || 0);
    }
    var sRect = slide.getBoundingClientRect();
    var cRect = container.getBoundingClientRect();
    return sRect.top - cRect.top - (container.clientTop || 0) + container.scrollTop;
  }

  var snapSuspended = false;
  var snapSettleTimer = 0;
  var gotoTargetTop = -1;
  var gotoAt = 0;

  function snapHost(): HTMLElement {
    return containerIsRoot ? doc.documentElement : (container as HTMLElement);
  }

  function currentScrollTop(): number {
    if (containerIsRoot) return win.pageYOffset || scrollRoot().scrollTop || 0;
    return container ? container.scrollTop : 0;
  }

  /**
   * 'auto' must really be instant: per spec it defers to the host's CSS
   * scroll-behavior, and reports commonly ship html{scroll-behavior:smooth}.
   * The jump would then animate, and the snap restore right after it
   * cancels the animation — the pane stays put and the index falls back.
   * Override the host's inline scroll-behavior for the duration of the call.
   */
  function rawScrollTo(top: number, behavior: ScrollBehavior): void {
    var target: any = containerIsRoot ? win : container;
    var host = snapHost();
    var instant = behavior === 'auto' && !!host;
    var prevBehavior = instant ? host.style.scrollBehavior : '';
    if (instant) host.style.scrollBehavior = 'auto';
    if (target && typeof target.scrollTo === 'function') {
      try {
        target.scrollTo({ top: top, behavior: behavior });
      } catch (e) {
        target.scrollTo(0, top);
      }
    } else if (container) {
      container.scrollTop = top;
    }
    if (instant) host.style.scrollBehavior = prevBehavior;
  }

  /**
   * Mandatory snap fights programmatic smooth scrolling in Chromium — the
   * snap logic cancels the animation and yanks back to the current slide.
   * Workaround: suspend snap for the animation, then restore once scrolling
   * has SETTLED (debounced on scroll events, not a fixed timer — restoring
   * mid-flight also cancels the animation). If the animation was aborted
   * anyway, jump straight to the target before re-arming snap.
   */
  function scheduleSnapRestore(): void {
    if (snapSettleTimer) win.clearTimeout(snapSettleTimer);
    snapSettleTimer = win.setTimeout(function () {
      snapSettleTimer = 0;
      if (!snapSuspended) return;
      var recent = Date.now() - gotoAt < 1600;
      if (
        recent &&
        gotoTargetTop >= 0 &&
        Math.abs(currentScrollTop() - gotoTargetTop) > 40
      ) {
        rawScrollTo(gotoTargetTop, 'auto');
      }
      snapSuspended = false;
      gotoTargetTop = -1;
      snapHost().style.scrollSnapType = 'y mandatory';
      // Index updates were held while the animation ran (it passes through
      // every slide in between); re-derive it from where the pane landed.
      syncIndex();
    }, 220) as unknown as number;
  }

  function goto(index: number): void {
    if (!snapMode || slides.length === 0) return;
    var clamped = Math.max(0, Math.min(slides.length - 1, index));
    var top = scrollTopOf(slides[clamped]);
    if (reduceMotion) {
      rawScrollTo(top, 'auto');
    } else {
      snapSuspended = true;
      gotoTargetTop = top;
      gotoAt = Date.now();
      snapHost().style.scrollSnapType = 'none';
      rawScrollTo(top, 'smooth');
      // Fallback: if no scroll events ever fire (already at target), settle.
      scheduleSnapRestore();
    }
    setIndex(clamped);
  }

  function setIndex(index: number): void {
    if (index === currentIndex) return;
    currentIndex = index;
    post({ type: 'slide', index: index, total: slides.length });
  }

  /**
   * The slide the pane is showing, derived from geometry alone (never from
   * the ORDER observer callbacks arrive in):
   *  1. scrolled to the very end → the last slide, if it is fully in view
   *     (a short closing slide can never reach the top edge);
   *  2. a slide whose top sits on the pane's top edge (where goto and snap
   *     put it) → that slide;
   *  3. otherwise the slide with the most visible height (ties → earlier),
   *     which also keeps a tall slide current while scrolling through it.
   */
  function activeIndex(): number {
    if (slides.length === 0) return -1;
    var viewTop = 0;
    var viewHeight = viewportHeight();
    if (!containerIsRoot && container) {
      viewTop = container.getBoundingClientRect().top + (container.clientTop || 0);
      viewHeight = container.clientHeight;
    }
    var viewBottom = viewTop + viewHeight;
    var last = slides.length - 1;
    var scrollTop = currentScrollTop();
    var maxScroll = containerIsRoot
      ? scrollRoot().scrollHeight - viewHeight
      : container
        ? container.scrollHeight - container.clientHeight
        : 0;
    if (scrollTop > 0 && maxScroll > 0 && scrollTop >= maxScroll - 2) {
      var lastRect = slides[last].getBoundingClientRect();
      if (lastRect.top >= viewTop - 2 && lastRect.bottom <= viewBottom + 2) {
        return last;
      }
    }
    var best = 0;
    var bestVisible = -1;
    for (var i = 0; i < slides.length; i++) {
      var r = slides[i].getBoundingClientRect();
      if (Math.abs(r.top - viewTop) <= 2) return i;
      var visible = Math.min(r.bottom, viewBottom) - Math.max(r.top, viewTop);
      if (visible > bestVisible + 1) {
        bestVisible = visible;
        best = i;
      }
    }
    return best;
  }

  /** Re-derive the index from geometry — skipped while a goto animates. */
  function syncIndex(): void {
    if (snapSuspended) return;
    var idx = activeIndex();
    if (idx !== -1) setIndex(idx);
  }

  // ---------- reveal replay ----------

  /**
   * Reports are stored with their author <script> sanitized away, but the
   * authoring pattern hides animation units behind `.reveal-init` /
   * `[data-reveal]` (opacity 0 until a script adds `.reveal`). Replay that
   * convention here: body gets `anim`, units get `reveal` as they enter the
   * viewport — so charts/cards animate in exactly as authored, scriptlessly.
   */
  function setupReveal(): void {
    var units = toArray(doc.querySelectorAll('.reveal-init, [data-reveal], .card'));
    if (units.length === 0) return;
    warnings.push('reveal-replayed:' + units.length);
    var reveal = function (el: HTMLElement) {
      if (el.classList && !el.classList.contains('reveal')) {
        el.classList.add('reveal');
      }
    };
    if (reduceMotion || typeof win.IntersectionObserver !== 'function') {
      // No motion (or no observer): show everything at rest state.
      for (var r = 0; r < units.length; r++) reveal(units[r]);
      return;
    }
    if (doc.body.classList) doc.body.classList.add('anim');
    var io = new win.IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            reveal(entries[i].target as HTMLElement);
            io.unobserve(entries[i].target);
          }
        }
      },
      { threshold: 0.15 }
    );
    for (var u = 0; u < units.length; u++) io.observe(units[u]);
  }

  /**
   * Edit mode can't wait for scroll-triggered reveals (authoring hides units
   * at opacity 0) — an invisible node is impossible to click-edit. Force every
   * reveal unit to its rest/visible state up front.
   */
  function revealAllAtRest(): void {
    var units = toArray(doc.querySelectorAll('.reveal-init, [data-reveal], .card'));
    for (var r = 0; r < units.length; r++) {
      if (units[r].classList && !units[r].classList.contains('reveal')) {
        units[r].classList.add('reveal');
      }
    }
  }

  // ---------- expandable deep-dive blocks ----------

  function injectRuntimeStyles(): void {
    var style = doc.createElement('style');
    style.setAttribute('data-tara-runtime', '1');
    style.textContent =
      '.tara-expand{border:1px solid var(--r-line,rgba(128,128,128,.25));' +
      'border-radius:14px;margin:18px 0;overflow:hidden;' +
      'background:var(--r-panel,transparent)}' +
      '.tara-expand__toggle{display:flex;align-items:center;gap:10px;width:100%;' +
      'padding:14px 18px;background:none;border:0;cursor:pointer;' +
      'font:inherit;font-weight:600;text-align:left;' +
      'color:var(--r-accent,inherit)}' +
      '.tara-expand__toggle:hover{filter:brightness(1.12)}' +
      '.tara-expand__chev{display:inline-block;transition:transform .2s ease-out;' +
      'font-size:.8em;line-height:1}' +
      '.tara-expand--open .tara-expand__chev{transform:rotate(90deg)}' +
      '.tara-expand__body{display:grid;grid-template-rows:0fr;' +
      'transition:grid-template-rows .22s ease-out}' +
      '.tara-expand--open .tara-expand__body{grid-template-rows:1fr}' +
      '.tara-expand__inner{overflow:hidden;min-width:0}' +
      '.tara-expand__content{padding:0 18px 18px}' +
      '@media (prefers-reduced-motion: reduce){' +
      '.tara-expand__body,.tara-expand__chev{transition:none}}';
    doc.head.appendChild(style);
  }

  function slideOf(el: HTMLElement): HTMLElement | null {
    var node: HTMLElement | null = el;
    while (node) {
      if (slides.indexOf(node) !== -1) return node;
      node = node.parentElement;
    }
    return null;
  }

  function setupExpandables(): void {
    var blocks = toArray(doc.querySelectorAll('[data-expandable]'));
    if (blocks.length === 0) return;
    if (!config.features || !config.features.deep_dive) {
      for (var h = 0; h < blocks.length; h++) blocks[h].style.display = 'none';
      return;
    }
    injectRuntimeStyles();
    for (var i = 0; i < blocks.length; i++) {
      (function (block: HTMLElement, idx: number) {
        var label = block.getAttribute('data-expand-label') || 'Deep dive';
        var shell = doc.createElement('div');
        shell.className = 'tara-expand';
        shell.setAttribute('data-tara-shell', '1');
        var toggle = doc.createElement('button');
        toggle.type = 'button';
        toggle.className = 'tara-expand__toggle';
        toggle.setAttribute('data-tara-toggle', '1');
        toggle.setAttribute('aria-expanded', 'false');
        var chev = doc.createElement('span');
        chev.className = 'tara-expand__chev';
        chev.textContent = '▸';
        var text = doc.createElement('span');
        text.textContent = label;
        toggle.appendChild(chev);
        toggle.appendChild(text);
        var body = doc.createElement('div');
        body.className = 'tara-expand__body';
        var inner = doc.createElement('div');
        inner.className = 'tara-expand__inner';
        var content = doc.createElement('div');
        content.className = 'tara-expand__content';
        inner.appendChild(content);
        body.appendChild(inner);
        shell.appendChild(toggle);
        shell.appendChild(body);
        var parent = block.parentElement;
        if (!parent) return;
        parent.insertBefore(shell, block);
        content.appendChild(block);
        toggle.addEventListener('click', function (ev) {
          ev.stopPropagation();
          var open = shell.className.indexOf('tara-expand--open') !== -1;
          shell.className = open ? 'tara-expand' : 'tara-expand tara-expand--open';
          toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
          var slide = slideOf(shell);
          if (slide) {
            // Expanded content usually overflows the page: relax the snap so
            // the interior stays reachable; restore when collapsed + short.
            if (!open) {
              slide.setAttribute('data-tara-expanded', '1');
              slide.style.scrollSnapStop = 'normal';
            } else {
              slide.removeAttribute('data-tara-expanded');
              relaxTallSlides(false);
            }
          }
          post({ type: 'expand', id: idx, expanded: !open });
        });
      })(blocks[i], i);
    }
  }

  // ---------- edit mode (agency workbench) ----------

  var editLeaves: HTMLElement[] = [];
  var editOriginals: string[] = [];

  /**
   * A "deepest text-bearing leaf": an element that has at least one non-empty
   * direct text child AND no element children other than <br>. This is the
   * only thing we ever make editable — never a container, never an <svg>
   * subtree node, never an interactive control. Editing these with
   * plaintext-only keeps the markup structurally frozen.
   */
  function isEditableLeaf(el: HTMLElement): boolean {
    var tag = el.tagName ? el.tagName.toLowerCase() : '';
    if (
      tag === 'script' ||
      tag === 'style' ||
      tag === 'svg' ||
      tag === 'a' ||
      tag === 'button' ||
      tag === 'input' ||
      tag === 'select' ||
      tag === 'textarea' ||
      tag === 'option' ||
      tag === 'title' ||
      tag === 'head' ||
      tag === 'meta' ||
      tag === 'link' ||
      tag === 'iframe'
    ) {
      return false;
    }
    // Never inside an <svg> subtree (its text nodes are graphical), and never
    // inside a runtime-injected shell.
    var p: HTMLElement | null = el.parentElement;
    while (p) {
      var ptag = p.tagName ? p.tagName.toLowerCase() : '';
      if (ptag === 'svg') return false;
      if (p.getAttribute && p.getAttribute('data-tara-shell') === '1') return false;
      p = p.parentElement;
    }
    var hasText = false;
    var hasElementChild = false;
    for (var i = 0; i < el.childNodes.length; i++) {
      var n = el.childNodes[i] as any;
      if (n.nodeType === 1) {
        var ctag = n.tagName ? n.tagName.toLowerCase() : '';
        if (ctag !== 'br') {
          hasElementChild = true;
          break;
        }
      } else if (n.nodeType === 3 && n.nodeValue && /\S/.test(n.nodeValue)) {
        hasText = true;
      }
    }
    return hasText && !hasElementChild;
  }

  function editDirtyCount(): number {
    var dirty = 0;
    for (var i = 0; i < editLeaves.length; i++) {
      if (editLeaves[i].textContent !== editOriginals[i]) dirty++;
    }
    return dirty;
  }

  function postEditState(): void {
    post({
      type: 'edit-state',
      dirty: editDirtyCount(),
      editable: editLeaves.length,
    });
  }

  function injectEditStyles(): void {
    var style = doc.createElement('style');
    style.setAttribute('data-tara-runtime', '1');
    style.textContent =
      '[data-tara-edit]{outline:1px dashed rgba(120,120,255,.35);' +
      'outline-offset:2px;border-radius:3px;cursor:text;' +
      'transition:outline-color .12s ease-out,background-color .12s ease-out}' +
      '[data-tara-edit]:hover{outline-color:rgba(120,120,255,.7);' +
      'background-color:rgba(120,120,255,.06)}' +
      '[data-tara-edit]:focus{outline:2px solid var(--r-accent,#5b7cfa);' +
      'background-color:rgba(120,120,255,.10)}';
    doc.head.appendChild(style);
  }

  function setupEditMode(): void {
    var all = toArray(doc.querySelectorAll('body *'));
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (!isEditableLeaf(el)) continue;
      // plaintext-only is the crux: the browser refuses rich edits, so the
      // node's markup can never be corrupted — only its text run changes.
      el.setAttribute('contenteditable', 'plaintext-only');
      el.setAttribute('data-tara-edit', '1');
      el.setAttribute('spellcheck', 'false');
      editLeaves.push(el);
      editOriginals.push(el.textContent || '');
    }
    injectEditStyles();
    warnings.push('edit-leaves:' + editLeaves.length);
    doc.addEventListener('input', function (ev) {
      var t = ev.target as HTMLElement | null;
      if (t && t.getAttribute && t.getAttribute('data-tara-edit') === '1') {
        postEditState();
      }
    });
    postEditState();
  }

  function revertAllEdits(): void {
    for (var i = 0; i < editLeaves.length; i++) {
      editLeaves[i].textContent = editOriginals[i];
    }
    postEditState();
  }

  /**
   * Serialize the CURRENT document (including the live plaintext edits) and
   * post it to the parent. We deliberately send the raw outerHTML — the parent
   * (report-edit-serialize.ts) strips every injected artifact
   * (contenteditable, data-tara-*, runtime <script>/<style>, expand shells)
   * before POSTing the new version, and the server re-sanitizes on top.
   */
  function serializeForSave(): void {
    var html = '<!doctype html>' + doc.documentElement.outerHTML;
    post({ type: 'serialized', html: html, dirty: editDirtyCount() });
  }

  // ---------- input ----------

  function isInteractive(el: HTMLElement | null): boolean {
    var node = el;
    while (node) {
      var tag = node.tagName ? node.tagName.toLowerCase() : '';
      if (
        tag === 'a' ||
        tag === 'button' ||
        tag === 'input' ||
        tag === 'select' ||
        tag === 'textarea' ||
        tag === 'summary' ||
        node.hasAttribute('data-expandable') ||
        node.hasAttribute('data-tara-toggle') ||
        node.hasAttribute('data-tara-shell')
      ) {
        return true;
      }
      node = node.parentElement;
    }
    return false;
  }

  function onKeydown(ev: KeyboardEvent): void {
    if (!snapMode || slides.length === 0) return;
    var t = ev.target as HTMLElement | null;
    if (t && (isInteractive(t) || t.isContentEditable)) return;
    var key = ev.key;
    var handled = true;
    if (key === 'ArrowDown' || key === 'ArrowRight' || key === 'PageDown') {
      goto(currentIndex + 1);
    } else if (key === ' ' || key === 'Spacebar') {
      goto(ev.shiftKey ? currentIndex - 1 : currentIndex + 1);
    } else if (key === 'ArrowUp' || key === 'ArrowLeft' || key === 'PageUp') {
      goto(currentIndex - 1);
    } else if (key === 'Home') {
      goto(0);
    } else if (key === 'End') {
      goto(slides.length - 1);
    } else {
      handled = false;
    }
    if (handled) ev.preventDefault();
  }

  function onClick(ev: MouseEvent): void {
    var target = ev.target as HTMLElement | null;
    // Link rewriting first: sandbox blocks navigation anyway; the parent
    // vets http(s) and window.opens.
    var node: HTMLElement | null = target;
    while (node && node.tagName && node.tagName.toLowerCase() !== 'a') {
      node = node.parentElement;
    }
    if (node && node.tagName && node.tagName.toLowerCase() === 'a') {
      var anchor = node as HTMLAnchorElement;
      var rawHref = anchor.getAttribute('href') || '';
      ev.preventDefault();
      if (rawHref.charAt(0) === '#') {
        var dest = rawHref.length > 1 ? doc.getElementById(rawHref.slice(1)) : null;
        if (dest) {
          var destSlide = slideOf(dest as HTMLElement);
          if (destSlide) goto(slides.indexOf(destSlide));
          else if (typeof (dest as any).scrollIntoView === 'function') {
            (dest as any).scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' });
          }
        }
        return;
      }
      var href = anchor.href || rawHref;
      if (/^https?:/i.test(href)) post({ type: 'link', href: href });
      return;
    }
    // Click zones (view mode only): bottom 12% → next, top 8% → prev.
    if (!snapMode || slides.length === 0 || config.mode !== 'view') return;
    if (isInteractive(target)) return;
    var vh = viewportHeight();
    if (vh <= 0) return;
    if (ev.clientY >= vh * 0.88) goto(currentIndex + 1);
    else if (ev.clientY <= vh * 0.08) goto(currentIndex - 1);
  }

  // ---------- index tracking ----------

  function observe(): void {
    if (!snapMode || slides.length === 0) return;
    // Both the observer and the scroll listener are only TRIGGERS: the index
    // itself always comes from activeIndex(). Taking it from the entries
    // made the result depend on callback order — the first callback reports
    // every observed slide, so whichever matched last won.
    var pending = false;
    var scheduleSync = function () {
      if (pending) return;
      pending = true;
      var settle = function () {
        pending = false;
        syncIndex();
      };
      if (typeof win.requestAnimationFrame === 'function') {
        win.requestAnimationFrame(settle);
      } else {
        win.setTimeout(settle, 32);
      }
    };
    if (typeof win.IntersectionObserver === 'function') {
      var io = new win.IntersectionObserver(scheduleSync, {
        root: containerIsRoot ? null : container,
        threshold: [0, 0.25, 0.5, 0.75, 1],
      });
      for (var i = 0; i < slides.length; i++) io.observe(slides[i]);
    }
    var scrollHost: any = containerIsRoot ? win : container;
    if (scrollHost && typeof scrollHost.addEventListener === 'function') {
      scrollHost.addEventListener(
        'scroll',
        function () {
          if (snapSuspended) scheduleSnapRestore();
          scheduleSync();
        },
        { passive: true }
      );
    }
  }

  // ---------- boot ----------

  var editMode = config.mode === 'edit';

  function boot(): void {
    discover();
    // Scroll/observe/snap on the element that really scrolls (see
    // findScrollHost), not on a non-scrolling wrapper around the slides.
    if (slides.length > 0) container = findScrollHost(container) || scrollRoot();
    containerIsRoot =
      !container ||
      container === doc.body ||
      container === doc.documentElement ||
      container === doc.scrollingElement;

    if (editMode) {
      // No snap paging / reveal animation while editing — they fight the caret
      // and hide (opacity 0) the very text runs the user wants to click-edit.
      snapMode = false;
      revealAllAtRest();
      setupEditMode();
    } else {
      normalise();
      setupReveal();
      setupExpandables();
      observe();
    }

    doc.addEventListener('keydown', onKeydown);
    doc.addEventListener('click', onClick);
    win.addEventListener('resize', function () {
      relaxTallSlides(false);
    });
    win.addEventListener('load', function () {
      relaxTallSlides(false);
    });
    win.addEventListener('message', function (ev: MessageEvent) {
      var d: any = ev.data;
      if (!d || d.source !== 'tara-portal') return;
      if (d.type === 'goto' && typeof d.index === 'number') goto(d.index);
      else if (d.type === 'request-serialize') serializeForSave();
      else if (d.type === 'revert-all') revertAllEdits();
    });

    if (slides.length > 0) setIndex(0);
    post({ type: 'compat', slides: slides.length, warnings: warnings });
  }

  /**
   * Boot once the frame has a layout box. The report frame is a sandboxed
   * srcdoc document with an opaque origin, which Chromium may run out of
   * process: it then parses — and this script runs — before the frame has
   * been given a size, so every box measures 0. findScrollHost would see a
   * wrapper that "does not overflow" and fall back to the document even when
   * the wrapper is the real scroller (Next/Previous then scroll nothing and
   * the index snaps back to 0), and tier-2 discovery would find no page-tall
   * children. Wait for the first size (resize event, polled as a safety
   * net); after ~3s boot anyway so a frame that stays hidden still pages.
   */
  var booted = false;
  function start(): void {
    if (booted) return;
    booted = true;
    win.removeEventListener('resize', onSized);
    boot();
  }
  function onSized(): void {
    if (viewportHeight() > 0) start();
  }
  if (viewportHeight() > 0) {
    start();
  } else {
    win.addEventListener('resize', onSized);
    var polls = 0;
    var poll = function () {
      if (booted) return;
      if (viewportHeight() > 0 || ++polls >= 60) start();
      else win.setTimeout(poll, 50);
    };
    win.setTimeout(poll, 50);
  }
}
